import React from "react";
import { CheckCircle2, Circle, FileText, UserPlus, Trophy, Clock } from "lucide-react";

/**
 * Onboarding timeline rendered on the member profile.
 * 7 steps. Highlights current stage and shows past stages as completed.
 */
const STAGES = [
  { key: "lead",                label: "Lead captured",       icon: UserPlus },
  { key: "trial_scheduled",     label: "Trial scheduled",     icon: Clock },
  { key: "trial_completed",     label: "Trial completed",     icon: CheckCircle2 },
  { key: "ready_to_onboard",    label: "Ready to onboard",    icon: FileText },
  { key: "membership_active",   label: "Active member",       icon: Trophy },
];

const ORDER = STAGES.map(s => s.key);

function stageRank(key) {
  const i = ORDER.indexOf(key);
  return i === -1 ? ORDER.length - 1 : i;
}

export default function OnboardingTimeline({ stage, history = [] }) {
  const currentRank = stageRank(stage);

  // Build a quick lookup: which stages have been visited (from history)
  const visited = new Set(history.map(h => h.stage));
  visited.add(stage);

  return (
    <section data-testid="onboarding-timeline" className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-base font-semibold text-foreground">Onboarding</h3>
          <p className="text-xs text-muted-foreground/80 mt-0.5">7-step pipeline · auto-updates from lead actions</p>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/50">
          {STAGES[currentRank]?.label}
        </span>
      </div>

      <ol className="space-y-3" data-testid="onboarding-steps">
        {STAGES.map((s, idx) => {
          const isCurrent = s.key === stage;
          const isDone = idx < currentRank || visited.has(s.key) && idx <= currentRank;
          const Icon = s.icon;
          const histEntry = history.find(h => h.stage === s.key);
          const ts = histEntry?.at;
          const tsStr = ts ? new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null;
          return (
            <li
              key={s.key}
              data-testid={`onboarding-step-${s.key}`}
              className={`flex items-start gap-3 ${isCurrent ? "" : "opacity-90"}`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border ${
                isDone
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : isCurrent
                  ? "bg-primary border-primary text-primary-foreground"
                  : "bg-muted/40 border-border text-muted-foreground/60"
              }`}>
                {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${
                    isDone ? "text-foreground" : isCurrent ? "text-foreground" : "text-muted-foreground/70"
                  }`}>
                    {s.label}
                  </span>
                  {isCurrent && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary text-primary-foreground">
                      Current
                    </span>
                  )}
                </div>
                {tsStr && (
                  <div className="text-[11px] text-muted-foreground/70 mt-0.5">{tsStr}{histEntry?.notes ? ` · ${histEntry.notes}` : ""}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
